import React from 'react';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import axiosClient from '@/utils/client';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CaretRight as CaretRightIcon,
  DotsThree as DotsThreeIcon,
  File as FileIcon,
  FileImage,
  FilePdf,
  Folder as FolderIconPhosphor,
  FolderPlus as FolderPlusIcon,
  House as HouseIcon,
  Lock as LockIcon,
  UploadSimple as UploadIcon,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';

import { useNavigate, useParams } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';

import type { CommunityFile } from '@/types/community';
import type { File } from '@/types/file';
import type { Folder } from '@/types/folder';
import { paths } from '@/paths';
import ViewDocumentDialog from '@/pages/dashboard/dialogs/ViewDocument';
import { useUser } from '@/hooks/v2/use-user';
import { useCommunityRole } from '@/hooks/v2/use-community-role';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import { Seo } from '@/components/seo';

import { CreateFolderDialog } from '../components/create-folder-modal';
import { EditFolderDialog } from '../components/edit-folder-modal';
import { EditFileDialog } from '../components/edit-file-modal';
import { UploadFileDialog } from '../components/upload-file-modal';

function getFileIcon(mimeType?: string): React.ReactNode {
  if (!mimeType) return <FileIcon size={20} />;
  if (mimeType.includes('pdf')) return <FilePdf size={20} />;
  if (mimeType.includes('image') || mimeType.includes('jpeg') || mimeType.includes('png'))
    return <FileImage size={20} />;
  return <FileIcon size={20} />;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'text.disabled',
        px: 3,
        py: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}

interface FolderRowProps {
  folder: any;
  onNavigate: (uuid: string) => void;
  onEdit: (item: any) => void;
  showActions: boolean;
}

function FolderRow({ folder, onNavigate, onEdit, showActions }: FolderRowProps) {
  const isPrivate = folder.visibility === 'private';

  return (
    <Stack
      direction="row"
      onClick={() => folder.uuid && onNavigate(folder.uuid)}
      sx={{
        alignItems: 'center',
        px: 3,
        py: 1.5,
        cursor: 'pointer',
        transition: 'background-color 150ms',
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      <Box sx={{ color: 'warning.main', display: 'flex', alignItems: 'center', mr: 2, flexShrink: 0 }}>
        <FolderIconPhosphor size={22} weight="fill" />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography noWrap sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
            {folder.name}
          </Typography>
          {isPrivate && (
            <Tooltip title="Private — only visible to managers/admins">
              <Box sx={{ display: 'flex', color: 'text.disabled' }}>
                <LockIcon size={13} />
              </Box>
            </Tooltip>
          )}
        </Stack>
        {folder.item_count > 0 && (
          <Typography color="text.secondary" variant="caption">
            {folder.item_count} item{folder.item_count !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>

      {showActions && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onEdit({ ...folder, type: 'folder' });
          }}
          size="small"
        >
          <DotsThreeIcon size={18} />
        </IconButton>
      )}
    </Stack>
  );
}

interface FileRowProps {
  file: any;
  onView: (file: any) => void;
  onEdit: (item: any) => void;
  showActions: boolean;
}

function FileRow({ file, onView, onEdit, showActions }: FileRowProps) {
  const isPrivate = file.visibility === 'private';

  return (
    <Stack
      direction="row"
      onClick={() => onView(file)}
      sx={{
        alignItems: 'center',
        px: 3,
        py: 1.5,
        cursor: 'pointer',
        transition: 'background-color 150ms',
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', mr: 2, flexShrink: 0 }}>
        {getFileIcon(file.mime_type)}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography noWrap sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
            {file.name_original || file.name}
          </Typography>
          {isPrivate && (
            <Tooltip title="Private">
              <Box sx={{ display: 'flex', color: 'text.disabled' }}>
                <LockIcon size={13} />
              </Box>
            </Tooltip>
          )}
        </Stack>
      </Box>

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        {file.mime_type && (
          <Chip label={file.mime_type.split('/').pop()} size="small" sx={{ fontSize: '0.7rem', height: 20 }} variant="outlined" />
        )}
        {showActions && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onEdit({ ...file, type: 'file' });
            }}
            size="small"
          >
            <DotsThreeIcon size={18} />
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
}

export function DocumentPage(): React.JSX.Element {
  const { user } = useUser();
  // Documents deliberately uses hasBoardLevelAccess, not hasManagerAccess:
  // routes/api/v1/folders.php admits board_member for folder create/update/
  // delete and file upload, and the header's New Folder/Upload buttons are
  // RoleGuard-gated (which elevates them). Using hasManagerAccess here let a
  // board member create a folder but gave them no row edit/delete actions.
  const { hasBoardLevelAccess } = useCommunityRole();
  const { folderId } = useParams();
  const navigate = useNavigate();
  const [selectedDocument, setSelectedDocument] = React.useState<CommunityFile | null>(null);
  const [editTarget, setEditTarget] = React.useState<any>(null);

  const [folderDialogOpen, setFolderDialogOpen] = React.useState(false);
  const [fileDialogOpen, setFileDialogOpen] = React.useState(false);
  const [viewDocumentOpen, setViewDocumentOpen] = React.useState(false);
  const [editFolderOpen, setEditFolderOpen] = React.useState(false);
  const [editFileOpen, setEditFileOpen] = React.useState(false);

  const { data, refetch, error, isLoading } = useQuery({
    queryKey: ['folders', folderId],
    queryFn: async () => {
      try {
        const response = await axiosClient.get(`/api/v1/folders/${folderId}`);
        return response.data;
      } catch (err: any) {
        if (err.response?.status === 404) {
          throw new Error('Folder not found');
        }
        throw err;
      }
    },
    retry: false,
  });

  const folders = data?.folders || [];
  const files = data?.files || [];
  const allItems = [...folders, ...files];
  const parent = data?.parent;
  const ancestors = data?.ancestors;

  const handleNavigateFolder = (uuid: string) => {
    navigate(paths.documents.folder(uuid));
  };

  const handleViewFile = (file: any) => {
    setSelectedDocument(file as unknown as CommunityFile);
    setViewDocumentOpen(true);
  };

  const handleEditItem = (item: any) => {
    setEditTarget(item);
    if (item.type === 'folder') {
      setEditFolderOpen(true);
    } else {
      setEditFileOpen(true);
    }
  };

  const handleRefetch = async () => {
    await refetch();
  };

  // Build ordered ancestors array from the API response
  const orderedAncestors = React.useMemo(() => {
    if (!ancestors) return [];
    return Object.entries(ancestors as Record<string, { name: string; id: string }>)
      .sort(([keyA], [keyB]) => Number(keyA) - Number(keyB))
      .map(([, value]) => value)
      .reverse();
  }, [ancestors]);

  // Not found state
  if (!isLoading && error?.message === 'Folder not found') {
    return (
      <>
        <Seo title="Folder Not Found" />
        <Box component="main" sx={{ flexGrow: 1, backgroundColor: 'background.paper', minHeight: '100vh' }}>
          <Container maxWidth="xl" sx={{ py: 10, textAlign: 'center' }}>
            <FolderIconPhosphor color="var(--mui-palette-text-disabled)" size={64} style={{ marginBottom: 16 }} />
            <Typography gutterBottom sx={{ fontWeight: 600 }} variant="h5">
              Folder not found
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4 }} variant="body2">
              The folder you're looking for doesn't exist or has been removed.
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'center' }}>
              <Button onClick={() => window.history.back()} variant="outlined">
                Go Back
              </Button>
              <Button href={paths.documents.index} variant="contained">
                All Documents
              </Button>
            </Stack>
          </Container>
        </Box>
      </>
    );
  }

  // No community selected
  if (user?.selected_community_id === null) {
    return (
      <>
        <Seo title="Documents" />
        <Box component="main" sx={{ flexGrow: 1, backgroundColor: 'background.paper', minHeight: '100vh' }}>
          <Container maxWidth="xl" sx={{ py: 10, textAlign: 'center' }}>
            <FolderIconPhosphor color="var(--mui-palette-text-disabled)" size={64} style={{ marginBottom: 16 }} />
            <Typography sx={{ fontWeight: 600, mb: 1 }} variant="h5">
              Select a Community
            </Typography>
            <Typography color="text.secondary" variant="body2">
              To view documents, you must be viewing as a single community.
            </Typography>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <Seo title={parent?.name ?? 'Documents'} />
      <Box component="main" sx={{ flexGrow: 1, backgroundColor: 'background.paper', minHeight: '100vh' }}>
        {/* Header strip */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: { xs: 2, md: 4 }, py: 3 }}>
          <Container disableGutters maxWidth="xl">
            {/* Breadcrumbs */}
            <Breadcrumbs
              separator={<CaretRightIcon size={12} />}
              sx={{ mb: 1.5, '& .MuiBreadcrumbs-separator': { mx: 0.5, color: 'text.disabled' } }}
            >
              <Link
                color="inherit"
                component={RouterLink}
                sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', fontSize: '0.8125rem', '&:hover': { color: 'primary.main' } }}
                to={paths.documents.index}
                underline="none"
              >
                <HouseIcon size={14} style={{ marginRight: 4 }} />
                Documents
              </Link>
              {orderedAncestors.map((ancestor) => (
                <Link
                  color="inherit"
                  component={RouterLink}
                  key={ancestor.id}
                  sx={{ color: 'text.secondary', fontSize: '0.8125rem', '&:hover': { color: 'primary.main' } }}
                  to={paths.documents.folder(ancestor.id)}
                  underline="none"
                >
                  {ancestor.name}
                </Link>
              ))}
              {parent && (
                <Typography color="text.primary" sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                  {parent.name}
                </Typography>
              )}
            </Breadcrumbs>

            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
                  {isLoading ? <Skeleton width={200} /> : parent?.name}
                </Typography>
              </Box>
              <RoleGuard
                requiredAccountRoles={[ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER, ACCOUNT_ROLES.ACCOUNT_OWNER]}
                requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
              >
                <Stack direction="row" spacing={1}>
                  <Button
                    onClick={() => setFolderDialogOpen(true)}
                    size="small"
                    startIcon={<FolderPlusIcon size={16} />}
                    variant="outlined"
                  >
                    New Folder
                  </Button>
                  <Button
                    onClick={() => setFileDialogOpen(true)}
                    size="small"
                    startIcon={<UploadIcon size={16} />}
                    variant="contained"
                  >
                    Upload
                  </Button>
                </Stack>
              </RoleGuard>
            </Stack>
          </Container>
        </Box>

        {/* Body */}
        <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 4 } }}>
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            {isLoading ? (
              <Stack spacing={0}>
                {[1, 2, 3].map((i) => (
                  <Box key={i} sx={{ px: 3, py: 1.5 }}>
                    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                      <Skeleton height={22} variant="circular" width={22} />
                      <Skeleton height={20} variant="text" width="40%" />
                    </Stack>
                  </Box>
                ))}
              </Stack>
            ) : allItems.length === 0 ? (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <FolderIconPhosphor color="var(--mui-palette-text-disabled)" size={48} style={{ marginBottom: 12 }} />
                <Typography color="text.secondary" sx={{ fontWeight: 500 }} variant="body1">
                  This folder is empty
                </Typography>
                <Typography color="text.disabled" sx={{ mt: 0.5 }} variant="body2">
                  Create a subfolder or upload files to get started
                </Typography>
              </Box>
            ) : (
              <>
                {folders.length > 0 && (
                  <>
                    <SectionLabel>Folders</SectionLabel>
                    <Divider />
                    {folders.map((folder: any, idx: number) => (
                      <React.Fragment key={folder.uuid || folder.id}>
                        <FolderRow folder={folder} onEdit={handleEditItem} onNavigate={handleNavigateFolder} showActions={hasBoardLevelAccess} />
                        {idx < folders.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </>
                )}

                {files.length > 0 && (
                  <>
                    {folders.length > 0 && <Divider sx={{ my: 0 }} />}
                    <SectionLabel>Files</SectionLabel>
                    <Divider />
                    {files.map((file: any, idx: number) => (
                      <React.Fragment key={file.id}>
                        <FileRow file={file} onEdit={handleEditItem} onView={handleViewFile} showActions={hasBoardLevelAccess} />
                        {idx < files.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </>
                )}
              </>
            )}
          </Paper>
        </Container>
      </Box>

      {/* Dialogs */}
      <CreateFolderDialog
        folderId={folderId}
        onClose={() => setFolderDialogOpen(false)}
        onCreate={async () => {
          await handleRefetch();
          setFolderDialogOpen(false);
        }}
        open={folderDialogOpen}
      />
      <UploadFileDialog
        folderId={folderId}
        onClose={() => setFileDialogOpen(false)}
        onCreate={async () => {
          await handleRefetch();
          setFileDialogOpen(false);
        }}
        open={fileDialogOpen}
      />
      <EditFolderDialog
        folder={editTarget as unknown as Folder}
        onClose={() => {
          setEditFolderOpen(false);
          setEditTarget(null);
        }}
        onEdit={async () => {
          await handleRefetch();
          setEditFolderOpen(false);
          setEditTarget(null);
        }}
        onSuccess={async () => {
          await handleRefetch();
          setEditFolderOpen(false);
          setEditTarget(null);
        }}
        open={editFolderOpen}
      />
      <EditFileDialog
        file={editTarget as unknown as File}
        onClose={() => {
          setEditFileOpen(false);
          setEditTarget(null);
        }}
        onEdit={async () => {
          await handleRefetch();
          setEditFileOpen(false);
          setEditTarget(null);
        }}
        onSuccess={async () => {
          await handleRefetch();
          setEditFileOpen(false);
          setEditTarget(null);
        }}
        open={editFileOpen}
      />
      <ViewDocumentDialog
        file={selectedDocument}
        onClose={() => {
          setSelectedDocument(null);
          setViewDocumentOpen(false);
        }}
        open={viewDocumentOpen}
      />
    </>
  );
}
